import { useCallback, useEffect, useRef, useState } from 'react';

// Margen mínimo (px) que debe quedar siempre visible del modal dentro del viewport.
const DRAG_MARGIN = 48;

/**
 * Hace que un modal se pueda arrastrar por la pantalla agarrándolo desde su
 * cabecera, para poder ver contenido que quede tapado debajo.
 *
 * Uso:
 *   const { contentRef, dragHandleProps, contentStyle } = useDraggableModal(isOpen);
 *   <div ref={contentRef} style={contentStyle}>
 *     <div {...dragHandleProps}>...cabecera...</div>
 *
 * @param {*} resetKey - al cambiar (p.ej. isOpen pasando a true), reinicia la posición.
 */
export default function useDraggableModal(resetKey) {
  const contentRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    setOffset({ x: 0, y: 0 });
  }, [resetKey]);

  useEffect(() => {
    const handlePointerMove = (e) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const { startX, startY, originX, originY, rect } = drag;
      let left = rect.left + (e.clientX - startX);
      let top = rect.top + (e.clientY - startY);
      left = Math.min(Math.max(left, DRAG_MARGIN - rect.width), window.innerWidth - DRAG_MARGIN);
      top = Math.min(Math.max(top, 0), window.innerHeight - DRAG_MARGIN);
      setOffset({ x: originX + (left - rect.left), y: originY + (top - rect.top) });
    };
    const stopDragging = (e) => {
      if (dragRef.current && (!e || e.pointerId === dragRef.current.pointerId)) {
        dragRef.current = null;
      }
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
    };
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest && e.target.closest('button, a, input, select, textarea, [data-no-drag]')) return;
    const contentEl = contentRef.current;
    if (!contentEl) return;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
      rect: contentEl.getBoundingClientRect(),
    };
  }, [offset]);

  return {
    contentRef,
    dragHandleProps: {
      onPointerDown: handlePointerDown,
    },
    contentStyle: (offset.x !== 0 || offset.y !== 0)
      ? { transform: `translate(${offset.x}px, ${offset.y}px)` }
      : undefined,
  };
}
